import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Library, Sparkles, KeyRound } from 'lucide-react'
import { getColorClasses, type Series, type Manual } from '../config/series'
import { seriesConfig } from '../config/series.generated'
import ManualCard from '../components/cards/ManualCard'
import SeriesIcon from '../components/ui/SeriesIcon'
import {
  LEARN_LIBRARY_ONE_TIME_GBP,
  LEARN_SUBSCRIPTION_PRICE_GBP_YEAR,
} from '../config/subscription'

export default function SeriesPage() {
  const { seriesSlug } = useParams<{ seriesSlug: string }>()

  if (!seriesSlug) {
    return <div>Series not found</div>
  }

  const allManuals = seriesConfig.flatMap((series: Series) =>
    series.manuals.map((manual: Manual) => ({
      ...manual,
      seriesSlug: series.slug,
      seriesIcon: series.icon,
    }))
  )

  const totalManuals = allManuals.length

  if (seriesSlug === 'all') {
    return (
      <div className="min-h-screen">
        <section className="bg-gradient-to-br from-navy-800 to-navy-900 text-white">
          <div className="max-w-7xl mx-auto container-padding py-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <Library className="w-12 h-12 text-gold-400" />
                <h1 className="text-4xl font-bold">Complete Finance Library</h1>
              </div>
              <p className="text-xl opacity-80 mb-8">
                {totalManuals} professional minimanuals — unlock the full catalogue with a subscription or a single
                one-time purchase. We no longer sell modules individually.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-bold text-gold-400">{totalManuals}</div>
                  <div className="text-sm opacity-70">Minimanuals</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-bold text-gold-400">{seriesConfig.length}</div>
                  <div className="text-sm opacity-70">Series</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-green-400">£{LEARN_SUBSCRIPTION_PRICE_GBP_YEAR}</div>
                  <div className="text-sm opacity-70">per year (subscribe)</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-amber-300">£{LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)}</div>
                  <div className="text-sm opacity-70">one-time (full library)</div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-10 bg-gradient-to-r from-amber-500 to-amber-600">
          <div className="max-w-5xl mx-auto container-padding">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <KeyRound className="w-10 h-10 text-navy-800" />
                <div>
                  <h2 className="text-2xl font-bold text-navy-800">Unlock every minimanual</h2>
                  <p className="text-navy-700">
                    Annual Learn Library or one payment for lifetime catalogue access — same site, browser reading.
                  </p>
                </div>
              </div>
              <Link
                to="/subscribe"
                className="px-8 py-4 rounded-xl font-bold text-lg bg-navy-800 text-white hover:bg-navy-900 hover:scale-[1.02] transition-all text-center"
              >
                View plans &amp; checkout
              </Link>
            </div>
          </div>
        </section>

        <section className="py-12 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto container-padding">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Browse by series</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {seriesConfig.map((series: Series) => (
                <Link
                  key={series.slug}
                  to={`/series/${series.slug}`}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:border-gold-500/50 transition-colors flex items-center gap-3"
                >
                  <SeriesIcon icon={series.icon} size="md" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white text-sm">{series.title}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{series.manuals.length} modules</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto container-padding">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              All {totalManuals} minimanuals
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {allManuals.map(
                (manual: Manual & { seriesSlug: string; seriesIcon: string }, index: number) => (
                  <motion.div
                    key={`${manual.seriesSlug}-${manual.slug}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(0.05 * index, 1) }}
                  >
                    <ManualCard
                      manual={manual}
                      seriesSlug={manual.seriesSlug}
                      seriesIcon={manual.seriesIcon}
                    />
                  </motion.div>
                )
              )}
            </div>
          </div>
        </section>
      </div>
    )
  }

  const series = seriesConfig.find((s) => s.slug === seriesSlug) as Series | undefined

  if (!series) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Series not found: {seriesSlug}</h1>
          <Link to="/series/all" className="bg-blue-600 text-white px-4 py-2 rounded">
            View all minimanuals
          </Link>
        </div>
      </div>
    )
  }

  const colorClasses = getColorClasses(series.color ?? 'blue')
  const paidManuals = series.manuals.filter((m) => m.price !== 0 && !m.free)

  return (
    <div className="min-h-screen">
      <section className={`${colorClasses.bg} ${colorClasses.border} border-b`}>
        <div className="max-w-7xl mx-auto container-padding py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center mb-6">
              <SeriesIcon icon={series.icon} size="2xl" className="mr-6" />
              <div>
                <h1 className={`text-4xl font-bold ${colorClasses.text} mb-2`}>{series.title}</h1>
                <p className={`text-xl ${colorClasses.text} opacity-80`}>{series.description}</p>
              </div>
            </div>

            <div className={`flex flex-wrap gap-6 text-sm ${colorClasses.text} opacity-80`}>
              <span>{series.manuals.length} minimanuals in this series</span>
              {paidManuals.length > 0 && (
                <span>
                  {paidManuals.length} included in Learn Library — not sold separately
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {paidManuals.length > 0 && (
        <section className="py-8 bg-gradient-to-r from-navy-700 to-navy-800">
          <div className="max-w-5xl mx-auto container-padding">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <Sparkles className="w-10 h-10 text-gold-400 shrink-0" />
                <div>
                  <h2 className="text-xl font-bold">Access this series + the entire library</h2>
                  <p className="text-gray-300 text-sm mt-1">
                    £{LEARN_SUBSCRIPTION_PRICE_GBP_YEAR}/year subscription or £{LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)}{' '}
                    one-time for the full catalogue.
                  </p>
                </div>
              </div>
              <Link
                to="/subscribe"
                className="px-6 py-3 rounded-xl font-bold bg-gold-500 text-navy-900 hover:bg-gold-400 transition-all"
              >
                Get access
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-4">Minimanuals in this series</h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 text-center mb-12 max-w-2xl mx-auto">
              Free samples open instantly. Premium topics unlock with a Learn Library subscription or a one-time full-library
              purchase — we do not sell individual modules.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {series.manuals.map((manual, index) => (
                <motion.div
                  key={manual.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 * index }}
                >
                  <ManualCard manual={manual} seriesSlug={series.slug} seriesIcon={series.icon} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-8 border-t border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex justify-between items-center">
            <Link
              to="/series/all"
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
            >
              ← All minimanuals
            </Link>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {series.manuals.length} modules · {series.title}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
